import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { initMapFactory, initMultiMapFactory, queryMapFactory, DefaultFailureAction, DefaultSuccessAction, dataHookWithCredentialsFactory } from "../../common/reducers/utilities";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { dateRangeOverlap, maxDate, minDate } from "../../common/utils/dateUtils";
import { distinct, ensureArray } from "../../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = memberLeave => memberLeave.id;

export const leaveMgrSlice = createSlice({
    name: 'leaves',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            ensureArray(action.payload.data).forEach(leave => state.data[key(leave)] = leave);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(leave => state.data[key(leave)] = leave);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(leave => delete state.data[key(leave)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => leaveMgrSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'leaveDetails', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'leaveDetails');

const fetchLeave = aggregatingFetch(
    id => network => network.post('/api/v2/memberLeaves', { id }, leaveMgrSlice.actions.set, visualizeFailure('get')),
    ids => network => network.post('/api/v2/memberLeaves', { id: ids }, leaveMgrSlice.actions.set, visualizeFailure('get')),
    { primitiveKey: true }
)
const fetchLeaveInRange = aggregatingFetch (
    (start, end) => network => network.post('/api/v2/memberLeaves', { start, end }, leaveMgrSlice.actions.addOrUpdate, visualizeFailure('get')),
    keys => network => network.post('/api/v2/memberLeaves', { start: minDate(keys, 0), end: maxDate(keys, 1) }, leaveMgrSlice.actions.addOrUpdate, visualizeFailure('get'))
)
const fetchMemberLeaveInRange = aggregatingFetch(
    (memberids, start, end) => network => network.post('/api/v2/memberLeaves', { memberid: memberids, start, end }, leaveMgrSlice.actions.addOrUpdate, visualizeFailure('get')),
    keys => network => network.post('/api/v2/memberLeaves', { memberid: distinct(keys.map(key => key[0])), start: minDate(keys, 1), end: maxDate(keys, 2) }, leaveMgrSlice.actions.addOrUpdate, visualizeFailure('get'))
)
export const insertLeave = (network, data) => network.put('/api/v1/memberLeave', data, leaveMgrSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateLeave = (network, data) => network.post('/api/v1/memberLeave', data, leaveMgrSlice.actions.addOrUpdate, visualizeFailure('update'));
export const removeLeave = (network, data) => network.delete('/api/v1/memberLeave/' + encodeURIComponent(data.id), leaveMgrSlice.actions.remove, visualizeFailure('remove'));

// #endregion

// #region selectors
const selectLeaves = state => state.leaves.data;
const selectLeave = id => createSelector([selectLeaves], leaves => leaves[id]);
const selectLeavesById = ids => createSelector([selectLeaves], leaves => ids.reduce((result, id) => {
    if (leaves[id]) {
        result.push(leaves[id]);
    }
    return result;
}, []))

const selectLeaveInRange = (start, end) => createSelector([selectLeaves], leaves => Object.values(leaves).filter(l => dateRangeOverlap(l, { start, end })));
const selectMemberLeaveInRange = (memberid, start, end) => createSelector([selectLeaves], leaves => Object.values(leaves).filter(l => memberid === l.memberid && dateRangeOverlap(l, { start, end })));

const selectApprovedLeaveInRange = (start, end) => createSelector([selectLeaves], leaves => Object.values(leaves).filter(l => l.approval === 'approved' && dateRangeOverlap(l, { start, end })));
const selectApprovedMemberLeaveInRange = (memberid, start, end) => createSelector([selectLeaves], leaves => Object.values(leaves).filter(l => l.approval === 'approved' && memberid === l.memberid && dateRangeOverlap(l, { start, end })));
// #endregion

const listen = {
    memberLeaveAdminCreated: (leave, { dispatch, websocket }) => {
        markFetched(websocket, 'leave', key(leave))
        dispatch(leaveMgrSlice.actions.addOrUpdate(leave))
    },
    memberLeaveAdminUpdated: (leave, { dispatch }) => dispatch(leaveMgrSlice.actions.addOrUpdate(leave)),
    memberLeaveAdminRemoved: (leave, { dispatch }) => dispatch(leaveMgrSlice.actions.remove(leave))
}

const fullRoles = ['administrator','member manager'];
const readRoles = ['auditor','member auditor','rostering manager','rostering auditor','recurring rostering manager','recurring rostering auditor','training manager','training auditor']
const options = {
    cleanup: { root: leaveMgrSlice.name, all: state => Object.values(state.leaves), removeAll: leaveMgrSlice.actions.remove, key },
    condition: credentials => credentials.hasRole(fullRoles.concat(readRoles))
}


const leaveInitByID = initMapFactory(fetchLeave, listen);
registerEntityBasedFetch('leave', leaveInitByID);
export const [useMgrLeave] = dataHookWithCredentialsFactory(selectLeave, leaveInitByID, { ...options, defaultValue: null });
export const [useMgrLeaves] = dataHookWithCredentialsFactory(selectLeavesById, initMultiMapFactory(leaveInitByID, listen), options);
export const [useMgrLeaveInRange] = dataHookWithCredentialsFactory(selectLeaveInRange, queryMapFactory(fetchLeaveInRange, 'id', leaveInitByID, listen), options);
export const [useMgrMemberLeaveInRange] = dataHookWithCredentialsFactory(selectMemberLeaveInRange, queryMapFactory(fetchMemberLeaveInRange, 'id', leaveInitByID, listen), options);
export const [useMgrApprovedLeaveInRange] = dataHookWithCredentialsFactory(selectApprovedLeaveInRange, queryMapFactory(fetchLeaveInRange, 'id', leaveInitByID, listen), options);
export const [useMgrApprovedMemberLeaveInRange] = dataHookWithCredentialsFactory(selectApprovedMemberLeaveInRange, queryMapFactory(fetchMemberLeaveInRange, 'id', leaveInitByID, listen), options);

const reducer = { name: leaveMgrSlice.name, reducer: leaveMgrSlice.reducer };
export default reducer;